import { useMemo } from 'react'

import { Section } from '@factorialco/deprecated-design-system'
import {
  ComplianceExtraFieldDefaultSettingFragment,
  FinanceSettingsSalesPageDocument,
  SalesDocumentTemplateFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useActivities } from 'modules/core/lib/activities'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import { ListItemDefaultComplianceExtraFieldSetting } from './DefaultComplianceExtraFieldsSettingCollection/listItemDefaultComplianceExtraFieldSetting'
import { ListItemSalesDocumentTemplate } from './SalesDocumentTemplatesCollection/listItemSalesDocumentTemplate'
import {
  ListItemSerialSequence,
  SequenceItem,
} from './SerialSequencesCollection/listItemSerialSequence'
import { useArchiveSequences } from './SerialSequencesCollection/manageSequencesHook'
import { ListItemVerifactu } from './VerifactuCollection/listItemVerifactu'
import { ListItemEmpty } from './listItemEmpty'

const SalesSettingsPage = () => {
  const { company } = useCurrentSession()
  const { openActivity } = useActivities()
  const { archiveSequence, unarchiveSequence } = useArchiveSequences('FinanceSettingsSalesPage')
  const { data, loading, error, refetch } = useQuery(FinanceSettingsSalesPageDocument, {
    variables: {
      companyId: company?.id,
    },
    skip: !company,
  })

  const salesInvoicesManagementFeatureEnabled =
    data?.companies?.featureAvailabilitiesConnection?.nodes[0]?.available ?? false
  const spanishLegalEntities = useMemo(
    () => company?.legalEntities.filter((le) => le.country === 'es') ?? [],
    [company?.legalEntities]
  )
  const anySpanishLegalEntity = spanishLegalEntities.length > 0

  const showVerifactuLinkSection = salesInvoicesManagementFeatureEnabled && anySpanishLegalEntity

  const canManageFinancialSequences = data?.finance._policies.financialSequencesManage.can ?? false
  const canManageSalesDocumentTemplates =
    data?.finance._policies.salesDocumentTemplatesManage.can ?? false
  const canManageComplianceExtraFieldDefaultSettings =
    data?.finance._policies.complianceExtraFieldDefaultSettingsManage.can ?? false
  const canSeeSalesSettings = data?.finance._policies.settingsSeeSalesSettings.can ?? false
  const canManageTaxBridgeDatas = data?.finance._policies.taxBridgeDatasManage.can ?? false

  const salesDocumentTemplates = data?.finance.salesDocumentTemplates
  const invoiceSequences = data?.finance.salesInvoiceSequences ?? []
  const creditNoteSequences = data?.finance.creditNoteSequences ?? []
  const defaultComplianceExtraFieldsSettings =
    data?.finance.defaultComplianceExtraFieldsSettings ?? []
  const taxBridgeData = useMemo(() => data?.finance.taxBridgeData ?? [], [data])

  // Combine invoice and credit note sequences
  const allSequences = useMemo<SequenceItem[]>(
    () => [...invoiceSequences, ...creditNoteSequences],
    [invoiceSequences, creditNoteSequences]
  )

  const onNewSequenceClick = () => {
    openActivity('finance.serialSequenceCreate', {
      refetchQuery: 'FinanceSettingsSalesPage',
    })
  }

  const onUpdateNameClick = (sequence: SequenceItem) => {
    openActivity('finance.serialSequenceUpdateName', {
      refetchQuery: 'FinanceSettingsSalesPage',
      serialSequenceId: sequence.id.toString(),
    })
  }
  const onDeleteClick = (sequence: SequenceItem) => {
    openActivity('finance.serialSequenceDelete', {
      refetchQuery: 'FinanceSettingsSalesPage',
      serialSequenceId: sequence.id.toString(),
    })
  }

  const onNewSalesDocumentTemplateClick = () => {
    openActivity('finance.salesDocumentTemplateCreate', {
      refetchQuery: 'FinanceSettingsSalesPage',
    })
  }

  const onUpdateSalesDocumentTemplateClick = (
    salesDocumentTemplate: SalesDocumentTemplateFragment
  ) => {
    openActivity('finance.salesDocumentTemplateUpdate', {
      refetchQuery: 'FinanceSettingsSalesPage',
      salesDocumentTemplateId: salesDocumentTemplate.id.toString(),
    })
  }

  const onDeleteSalesDocumentTemplateClick = (
    salesDocumentTemplate: SalesDocumentTemplateFragment
  ) => {
    openActivity('finance.salesDocumentTemplateDelete', {
      refetchQuery: 'FinanceSettingsSalesPage',
      salesDocumentTemplateId: salesDocumentTemplate.id.toString(),
    })
  }

  const onNewDefaultComplianceSettingsClick = () => {
    openActivity('finance.defaultComplianceSettingsCreate', {
      refetchQuery: 'FinanceSettingsSalesPage',
    })
  }

  const onEditDefaultComplianceSettingsClick = (
    defaultComplianceExtraFieldSetting: ComplianceExtraFieldDefaultSettingFragment
  ) => {
    openActivity('finance.defaultComplianceSettingsUpdate', {
      refetchQuery: 'FinanceSettingsSalesPage',
      defaultComplianceExtraFieldSettingId: defaultComplianceExtraFieldSetting.id.toString(),
    })
  }
  const onDeleteDefaultComplianceSettingsClick = (
    defaultComplianceExtraFieldSetting: ComplianceExtraFieldDefaultSettingFragment
  ) => {
    openActivity('finance.defaultComplianceSettingsDelete', {
      refetchQuery: 'FinanceSettingsSalesPage',
      defaultComplianceExtraFieldSettingId: defaultComplianceExtraFieldSetting.id.toString(),
    })
  }

  return (
    <ResponsivePage.Body loading={loading} error={error} authorized={canSeeSalesSettings}>
      {canManageFinancialSequences && (
        <Section.List
          description={i18n.t('finance.serial_sequences.list.description')}
          illustration="multiple-buildings"
          title={i18n.t('finance.serial_sequences.list.title')}
          action={{
            label: i18n.t('finance.serial_sequences.list.new_sequence'),
            onClick: onNewSequenceClick,
          }}
        >
          {allSequences.map((sequence) => (
            <ListItemSerialSequence
              key={`${sequence.__typename}-${sequence.id}`}
              sequence={sequence}
              onEditName={() => onUpdateNameClick(sequence)}
              onArchive={() => archiveSequence(sequence.id)}
              onUnarchive={() => unarchiveSequence(sequence.id)}
              onDelete={() => onDeleteClick(sequence)}
            />
          ))}
          {allSequences.length === 0 && (
            <ListItemEmpty text={i18n.t('finance.serial_sequences.empty_state.message')} />
          )}
        </Section.List>
      )}
      {canManageSalesDocumentTemplates && (
        <Section.List
          description={i18n.t('finance.sales_document_templates.list.description')}
          illustration="template"
          title={i18n.t('finance.sales_document_templates.list.title')}
          action={{
            label: i18n.t('finance.sales_document_templates.list.new_template'),
            onClick: onNewSalesDocumentTemplateClick,
          }}
        >
          {salesDocumentTemplates &&
            salesDocumentTemplates.map((salesDocumentTemplate) => (
              <ListItemSalesDocumentTemplate
                key={salesDocumentTemplate.id}
                salesDocumentTemplate={salesDocumentTemplate}
                onEdit={() => onUpdateSalesDocumentTemplateClick(salesDocumentTemplate)}
                onDelete={() => onDeleteSalesDocumentTemplateClick(salesDocumentTemplate)}
              />
            ))}
          {!salesDocumentTemplates ||
            (salesDocumentTemplates.length === 0 && (
              <ListItemEmpty
                text={i18n.t('finance.sales_document_templates.empty_state.message')}
                title={i18n.t('finance.sales_document_templates.empty_state.title')}
              />
            ))}
        </Section.List>
      )}
      {canManageComplianceExtraFieldDefaultSettings && (
        <Section.List
          description={i18n.t('finance.compliance_extra_field_default_settings.list.description')}
          illustration="multiple-buildings"
          title={i18n.t('finance.compliance_extra_field_default_settings.list.title')}
          action={{
            label: i18n.t('finance.compliance_extra_field_default_settings.list.new'),
            onClick: onNewDefaultComplianceSettingsClick,
          }}
        >
          {defaultComplianceExtraFieldsSettings.length === 0 && (
            <ListItemEmpty
              text={i18n.t('finance.compliance_extra_field_default_settings.empty_state.message')}
              title={i18n.t('finance.compliance_extra_field_default_settings.empty_state.title')}
            />
          )}
          {defaultComplianceExtraFieldsSettings.map((defaultComplianceExtraFieldSetting) => (
            <ListItemDefaultComplianceExtraFieldSetting
              key={defaultComplianceExtraFieldSetting.id}
              defaultComplianceExtraFieldSetting={defaultComplianceExtraFieldSetting}
              onEdit={() =>
                onEditDefaultComplianceSettingsClick(defaultComplianceExtraFieldSetting)
              }
              onDelete={() =>
                onDeleteDefaultComplianceSettingsClick(defaultComplianceExtraFieldSetting)
              }
            />
          ))}
        </Section.List>
      )}
      {showVerifactuLinkSection && (
        <Section.FullWidth
          description={i18n.t('finance.sale_invoices.settings.description')}
          illustration="multiple-buildings"
          title={i18n.t('finance.sale_invoices.settings.title')}
          link={{
            label: i18n.t('finance.sale_invoices.settings.cta'),
            href: 'https://www.datocms-assets.com/58969/1765984183-declaracion-responsable-factorial-verifactu-3.pdf',
          }}
        ></Section.FullWidth>
      )}
      {canManageTaxBridgeDatas && salesInvoicesManagementFeatureEnabled && spanishLegalEntities.length > 0 && (
        <Section.List
        description={i18n.t('finance.sale_invoices.settings.verifactu.description')}
        illustration="settings"
        title={i18n.t('finance.sale_invoices.settings.verifactu.title')}
      >
        {spanishLegalEntities.map((legalEntity) => (
          <ListItemVerifactu
            key={legalEntity.id}
            legalEntityId={legalEntity.id}
            legalEntityName={legalEntity.legalName}
            taxBridgeData={taxBridgeData.find((taxBridgeDatum) => taxBridgeDatum.resourceId === legalEntity.id)}
            onUpdate={() => {setTimeout(() => {
              refetch()
            }, 10000)}}
          />
        ))}
      </Section.List>
    )}
    </ResponsivePage.Body>
  )
}

export default SalesSettingsPage
